import { useCallback, useEffect, useRef, useState } from "react";
import type { RagConversation, RagSource } from "../api/ragApi";
import { askAgent } from "../api/ragApi";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  sources?: RagSource[];
  error?: string;
}

interface UseRagChatOptions {
  onChunk?: (chunk: string) => void;
  /**
   * Past thread to open the chat on. Switching threads goes through a remount
   * (see WixarpediaChat's `key`), which is what keeps every other piece of
   * per-thread state (input draft, voice mode, dictation) from leaking across
   * threads. Handing back a NEWER copy of the SAME thread re-seeds the bubbles
   * in place instead — see the re-hydration effect below.
   */
  initialConversation?: RagConversation | null;
  /**
   * Fires with the id of a thread that was just opened server-side by the first
   * question of an unsaved chat. Lets the sidebar pick the new thread up.
   */
  onConversationStarted?: (conversationId: number) => void;
}

/**
 * Turns a stored thread into chat bubbles.
 *
 * These messages are seeded straight into state and NEVER routed through the
 * `onChunk` callback — which is the only path that feeds text-to-speech. That is
 * what stops reopening an old thread from making Sophie read the whole history
 * out loud: voice is reachable only from live streaming, by construction rather
 * than by a flag someone has to remember to check.
 */
function toChatMessages(conversation: RagConversation): ChatMessage[] {
  return conversation.messages.map((message, index) => ({
    id: `history-${conversation.id}-${index}`,
    role: message.role,
    text: message.text,
    sources: message.sources,
  }));
}

export function useRagChat(agentId: number | null, options: UseRagChatOptions = {}) {
  const { onChunk, initialConversation, onConversationStarted } = options;

  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    initialConversation ? toChatMessages(initialConversation) : [],
  );
  const [isStreaming, setIsStreaming] = useState(false);

  // A ref, not state: the id minted mid-exchange has to be visible to the NEXT
  // sendMessage without waiting for a re-render, and it must not re-create the
  // sendMessage callback while a stream is running.
  const conversationIdRef = useRef<number | null>(initialConversation?.id ?? null);

  // Mirrors isStreaming for the re-hydration effect, which must read the value
  // at the moment it fires rather than the one captured when it was declared.
  const isStreamingRef = useRef(false);

  // The copy of the thread already on screen. Anything else arriving from the
  // store is, by construction, fresher.
  const hydratedRef = useRef<RagConversation | null | undefined>(initialConversation);

  /**
   * Re-seeds the bubbles when the store hands back a newer copy of the SAME
   * thread — reopening it from the sidebar, or the pending-answer poll landing.
   *
   * Without this the pane was frozen at whatever the thread looked like when it
   * was first mounted: leaving Wixarpedia mid-generation and coming back showed
   * the thread minus the exchange that finished server-side while you were away,
   * and clicking the row again couldn't fix it (same `key`, so no remount). Only
   * a full page reload could.
   *
   * A live stream wins over the store: mid-answer the pane holds turns the
   * server hasn't filed yet, so an incoming copy is dropped rather than queued.
   * As with the mount path, messages are seeded straight into state and never
   * routed through `onChunk`, so re-hydrating still can't reach text-to-speech.
   */
  useEffect(() => {
    if (!initialConversation || initialConversation === hydratedRef.current) return;
    hydratedRef.current = initialConversation;
    if (isStreamingRef.current) return;
    setMessages(toChatMessages(initialConversation));
    conversationIdRef.current = initialConversation.id;
  }, [initialConversation]);

  const sendMessage = useCallback(
    async (question: string) => {
      if (!agentId || !question.trim() || isStreaming) return;

      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        text: question.trim(),
      };

      const assistantId = `assistant-${Date.now()}`;
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: "assistant",
        text: "",
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);
      isStreamingRef.current = true;

      try {
        await askAgent(
          agentId,
          question.trim(),
          // onChunk
          (chunk) => {
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, text: m.text + chunk } : m)),
            );
            onChunk?.(chunk);
          },
          // onSources
          (sources) => {
            setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, sources } : m)));
          },
          // onError
          (error) => {
            setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, error } : m)));
          },
          {
            conversationId: conversationIdRef.current,
            onConversationId: (id) => {
              const isNewThread = conversationIdRef.current === null;
              conversationIdRef.current = id;
              if (isNewThread) {
                onConversationStarted?.(id);
              }
            },
          },
        );
      } catch {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, error: "Failed to get a response. Please try again." }
              : m,
          ),
        );
      } finally {
        setIsStreaming(false);
        isStreamingRef.current = false;
      }
    },
    [agentId, isStreaming, onChunk, onConversationStarted],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    conversationIdRef.current = null;
    hydratedRef.current = null;
  }, []);

  return {
    messages,
    isStreaming,
    sendMessage,
    clearMessages,
  };
}
